import { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet trae íconos por URL — parchamos para evitar el bug de bundler
// (Vite/Rolldown no copia los PNG por default).
const icon = L.icon({
  iconUrl: 'data:image/svg+xml;utf8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48">
      <path fill="#9D2449" stroke="#fff" stroke-width="2"
        d="M16 0C7.2 0 0 7.2 0 16c0 10 16 32 16 32s16-22 16-32c0-8.8-7.2-16-16-16z"/>
      <circle cx="16" cy="16" r="6" fill="#fff"/>
    </svg>
  `),
  iconSize: [28, 42],
  iconAnchor: [14, 42],
  popupAnchor: [0, -36],
});

type Props = {
  latitud: number | null;
  longitud: number | null;
  onChange?: (lat: number, lon: number) => void;
  readOnly?: boolean;
  className?: string;
  height?: number;
};

function ClickCapture({ onChange }: { onChange?: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      if (onChange) onChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ lat, lon }: { lat: number | null; lon: number | null }) {
  const map = useMap();
  const last = useRef<string>('');
  useEffect(() => {
    if (lat == null || lon == null) return;
    const key = `${lat.toFixed(5)},${lon.toFixed(5)}`;
    if (key === last.current) return;
    last.current = key;
    map.flyTo([lat, lon], map.getZoom() < 14 ? 15 : map.getZoom(), { duration: 0.6 });
  }, [lat, lon, map]);
  return null;
}

export function MapaGPS({ latitud, longitud, onChange, readOnly, className, height = 320 }: Props) {
  // Default al centro aproximado de México si no hay coords
  const lat = latitud ?? 23.634501;
  const lon = longitud ?? -102.552784;
  const hasCoords = latitud != null && longitud != null;

  return (
    <div className={className}>
      <MapContainer
        center={[lat, lon]}
        zoom={hasCoords ? 15 : 5}
        scrollWheelZoom={!readOnly}
        style={{ height, width: '100%', borderRadius: 8 }}
        className="overflow-hidden border border-border"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {hasCoords && (
          <Marker position={[latitud!, longitud!]} icon={icon} />
        )}
        <Recenter lat={latitud} lon={longitud} />
        {!readOnly && <ClickCapture onChange={onChange} />}
      </MapContainer>
      {!readOnly && (
        <p className="mt-1.5 text-[11px] text-ink-soft">
          Haz click en el mapa para fijar la ubicación. Zoom con rueda del ratón.
        </p>
      )}
    </div>
  );
}
